// AdminDashboard.js — versión integrada (copiar/pegar)

import React, { useState, useEffect } from "react";
import { firestore, firebase } from "./firebase";
import { esCobros, etiquetaCuenta, ADMIN_EMAIL } from "./roles";
import { formatFechaUY } from "./fechasUY";
import { resumenPago, sumaPagos, estadoPorMontos, etiquetaEstado } from "./pagos";

import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import es from "date-fns/locale/es";
import {
  addMonths,
  startOfMonth,
  eachDayOfInterval,
  format,
  differenceInDays,
} from "date-fns";
import { hoyUY } from "./fechasUY";

import Swal from "sweetalert2";
import * as XLSX from "xlsx";

import "./AdminDashboard.css";
import AdminCancelaciones from "./AdminCancelaciones";

// Mailgun wrapper (tu función)
import { enviarMail } from "./apiEmail";

registerLocale("es", es);

function AdminDashboard({ usuario }) {
  const [reservas, setReservas] = useState([]);
  const [bloqueos, setBloqueos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filtroEstado, setFiltroEstado] = useState(""); // "" = todos | "pendiente" | "confirmado" | "rechazado"
  const [searchUsuario, setSearchUsuario] = useState("");
  const [casaSeleccionada, setCasaSeleccionada] = useState("");
  const [precioAlta,  setPrecioAlta]  = useState(3000); // ene/feb
  const [precioMedia, setPrecioMedia] = useState(2800); // dic/mar
  const [precioBaja,  setPrecioBaja]  = useState(2500); // resto
  const [precioCamping, setPrecioCamping] = useState(290); // por persona / noche
  const [precioDia, setPrecioDia] = useState(145);         // por persona / día
  const [rangoBloqueo, setRangoBloqueo] = useState([null, null]);
  const [rangoDesbloqueo, setRangoDesbloqueo] = useState([null, null]);
  // La cuenta de cobros solo ve la pestaña Pagos
  const soloCobros = esCobros(usuario);
  const [tabActivo, setTabActivo] = useState(soloCobros ? "pagos" : "reservas");
  const tabVisible = soloCobros ? "pagos" : tabActivo;
  const [subTabPagos, setSubTabPagos] = useState("casas");
  const [startDateBloqueo, setStartDateBloqueo] = useState(null);
  const [endDateBloqueo, setEndDateBloqueo] = useState(null);

  // NUEVO: registros (camping + día)
  const [registros, setRegistros] = useState([]);

  // helper para Timestamp/Date
  const toDate = (x) => (x && x.toDate ? x.toDate() : x || null);

  // clave única por colección+id (evita choques de ids entre colecciones)
  const keyOf = (coleccion, id) => `${coleccion}:${id}`;

  // === Contacto del huésped (desde la colección usuarios) ===
  const contactoPorEmail = React.useMemo(() => {
    const m = {};
    usuarios.forEach((u) => {
      if (u.email) m[u.email.trim().toLowerCase()] = u;
    });
    return m;
  }, [usuarios]);

  const celularDe = (email) =>
    contactoPorEmail[(email || "").trim().toLowerCase()]?.celular || "";

  // Arma el link de WhatsApp. Números uruguayos locales (09x...) se pasan a +598.
  const linkWhatsApp = (cel) => {
    let n = String(cel || "").replace(/\D/g, "");
    if (!n) return null;
    if (n.startsWith("00")) n = n.slice(2);
    if (n.startsWith("0")) n = "598" + n.slice(1);
    else if (n.length === 8 && n.startsWith("9")) n = "598" + n;
    return `https://wa.me/${n}`;
  };

  const renderContacto = (email) => {
    const e = (email || "").trim().toLowerCase();
    if (!e) return null;
    if (e === ADMIN_EMAIL) {
      return (
        <div className="pago-contacto pago-contacto--admin">
          Cargada desde la cuenta admin (sin contacto del huésped)
        </div>
      );
    }
    const cel = celularDe(email);
    const wa = linkWhatsApp(cel);
    return (
      <div className="pago-contacto">
        ✉️ <a href={`mailto:${email}`}>{email}</a>
        {cel && (
          <>
            {" · "}📱{" "}
            {wa ? (
              <a href={wa} target="_blank" rel="noopener noreferrer">
                {cel} (WhatsApp)
              </a>
            ) : (
              cel
            )}
          </>
        )}
      </div>
    );
  };

  // === Auditoría de pagos: quién hizo cada cambio y cuándo ===
  const cuentaActual = () =>
    window?.firebase?.auth()?.currentUser?.email || usuario?.email || "";

  const camposAuditoria = (estado, metodo, nota, extra = {}) => {
    const por = cuentaActual();
    const ahora = new Date();
    return {
      paymentUpdatedBy: por,
      paymentUpdatedAt: ahora,
      paymentHistory: firebase.firestore.FieldValue.arrayUnion({
        estado,
        metodo,
        nota,
        por,
        fecha: ahora.toISOString(),
        ...extra,
      }),
    };
  };

  const fechaHoraUY = (x) => {
    const f = x && x.toDate ? x.toDate() : x ? new Date(x) : null;
    return f && !isNaN(f) ? formatFechaUY(f, { hour: "2-digit", minute: "2-digit" }) : "";
  };

  const renderAuditoria = (item) => {
    if (item.paymentUpdatedBy) {
      return (
        <span className="pago-audit">
          Último cambio: <b>{etiquetaCuenta(item.paymentUpdatedBy)}</b>
          {item.paymentUpdatedAt ? ` · ${fechaHoraUY(item.paymentUpdatedAt)}` : ""}
        </span>
      );
    }
    if (item.paymentConfirmedBy) {
      return (
        <span className="pago-audit">
          Confirmado por <b>{etiquetaCuenta(item.paymentConfirmedBy)}</b>
          {item.paymentConfirmedAt ? ` · ${fechaHoraUY(item.paymentConfirmedAt)}` : ""}
        </span>
      );
    }
    return null;
  };

  const detallePagos = (item) =>
    (Array.isArray(item.pagos) ? item.pagos : [])
      .map(
        (p) =>
          `${fechaHoraUY(p.fecha)} UYU ${p.monto}${p.metodo ? ` ${p.metodo}` : ""}` +
          ` (${etiquetaCuenta(p.por)})${p.anulado ? " ANULADO" : ""}`
      )
      .join(" | ");

  const historialTexto = (item) =>
    (item.paymentHistory || [])
      .map(
        (h) =>
          `${fechaHoraUY(h.fecha)} ${etiquetaCuenta(h.por)}: ${h.estado}` +
          (h.monto != null ? ` (${h.anulado ? "anuló" : "pagó"} UYU ${h.monto})` : "") +
          (h.nota ? ` – ${h.nota}` : "")
      )
      .join(" | ");

  // === Helpers para mostrar registros en formato simple ===
  const d = (x) => (x && x.toDate ? x.toDate() : x || null);
  const f = (x) => (x ? format(x, "dd/MM/yyyy", { locale: es }) : "-");
  const money = (n) => `UYU ${Number(n || 0).toLocaleString("es-UY")}`;

  // Recalcula el costo de una reserva de casa según la temporada (mismo criterio que la web).
  // Usa el total guardado si existe; si no, lo recalcula con los precios actuales.
  const totalReservaCasa = (r) => {
    if (!r || !r.fechaInicio || !r.fechaFin) return 0;
    if (r.total != null) return Number(r.total) || 0;
    if (differenceInDays(r.fechaFin, r.fechaInicio) <= 0) return 0;
    const alta = Number(precioAlta ?? 3000);
    const media = Number(precioMedia ?? 2800);
    const baja = Number(precioBaja ?? 2500);
    const tarifa = (d) => {
      const m = d.getMonth();
      if (m === 0 || m === 1) return alta;
      if (m === 11 || m === 2) return media;
      return baja;
    };
    const noches = eachDayOfInterval({ start: r.fechaInicio, end: r.fechaFin }).slice(0, -1);
    return noches.reduce((acc, d) => acc + tarifa(d), 0);
  };

  // ====== PAGOS: total, pagado y saldo ======
  const totalDe = (item, coleccion) =>
    coleccion === "reservas" ? totalReservaCasa(item) : Number(item.total || 0);

  const resumenDe = (item, coleccion) => resumenPago(item, totalDe(item, coleccion));

  // Precio "normal" calculado con las tarifas actuales (para volver del monto acordado)
  const totalNormal = (item, coleccion) => {
    if (coleccion === "reservas") return totalReservaCasa({ ...item, total: null });
    const personas = Number(item.personas || 0);
    if (item.tipo === "camping") return personas * Number(item.noches || 1) * Number(precioCamping || 0);
    return personas * Number(precioDia || 0);
  };

  // Línea compacta para cada registro
  const lineaRegistro = (r) => {
    if (r.tipo === "camping") {
      const noches = r.noches || 1;
      return `${r.nombre || r.email} — Camping · ${r.personas} pers · ${noches} noche${noches > 1 ? "s" : ""} — ${f(d(r.inicio))} – ${f(d(r.fin))}`;
    }
    // Día
    return `${r.nombre || r.email} — Día · ${r.personas} pers — ${f(d(r.fecha))}`;
  };

  // Ordenar por creadoEn desc (más nuevos arriba)
  const registrosOrdenados = [...registros].sort(
    (a, b) =>
      (b.creadoEn?.getTime?.() || b.creadoEn?.toMillis?.() || 0) -
      (a.creadoEn?.getTime?.() || a.creadoEn?.toMillis?.() || 0)
  );

  // Eliminar registro (solo admin desde panel)
  const eliminarRegistro = async (id) => {
    const res = await Swal.fire({
      title: "¿Eliminar registro?",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "No",
    });
    if (!res.isConfirmed) return;
    try {
      await firestore.collection("registros").doc(id).delete();
      Swal.fire("Eliminado", "Registro eliminado.", "info");
    } catch {
      Swal.fire("Error", "No se pudo eliminar.", "error");
    }
  };

  // === Controles por fila de pago ===
  const [editMonto, setEditMonto] = useState({});
  const [editMetodo, setEditMetodo] = useState({});
  const [editNota, setEditNota] = useState({});
  const [editEnviarMail, setEditEnviarMail] = useState({});
  const setMonto = (id, v) => setEditMonto((s) => ({ ...s, [id]: v }));
  const setMetodo = (id, v) => setEditMetodo((s) => ({ ...s, [id]: v }));
  const setNota = (id, v) => setEditNota((s) => ({ ...s, [id]: v }));
  const setMail = (id, v) => setEditEnviarMail((s) => ({ ...s, [id]: v }));

  useEffect(() => {
    const unsubReservas = firestore.collection("reservas").onSnapshot((snapshot) => {
      const reservasData = snapshot.docs.map((doc) => {
        const d = doc.data();
        return {
          id: doc.id,
          ...d,
          fechaInicio: d.fechaInicio.toDate(),
          fechaFin: d.fechaFin.toDate(),
          paymentStatus: d.paymentStatus || "pendiente",
          paymentMethod: d.paymentMethod || "",
          paymentNote: d.paymentNote || "",
          paymentConfirmedAt: d.paymentConfirmedAt?.toDate ? d.paymentConfirmedAt.toDate() : d.paymentConfirmedAt || null,
          paymentConfirmedBy: d.paymentConfirmedBy || "",
          cancelRequested: !!d.cancelRequested,
        };
      });
      setReservas(reservasData.sort((a, b) => b.fechaInicio - a.fechaInicio));
    });

    const unsubBloqueos = firestore.collection("bloqueos").onSnapshot((snapshot) => {
      const bloqueosData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        fecha: doc.data().fecha.toDate(),
      }));
      setBloqueos(bloqueosData);
    });

    const unsubUsuarios = firestore.collection("usuarios").onSnapshot((snapshot) => {
      const usuariosData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsuarios(usuariosData.sort((a, b) => a.nombre.localeCompare(b.nombre)));
    });

    // 👇 NUEVO: escuchar registros (camping + día)
    const unsubRegistros = firestore.collection("registros").onSnapshot((snap) => {
      const arr = snap.docs.map((d) => {
        const raw = d.data();
        return {
          id: d.id,
          ...raw,
          creadoEn: toDate(raw.creadoEn),
          inicio: toDate(raw.inicio),
          fin: toDate(raw.fin),
          fecha: toDate(raw.fecha),
        };
      });
      setRegistros(arr);
    });

    const obtenerPrecios = async () => {
      const doc = await firestore.collection("config").doc("precios").get();
      const data = doc.exists ? doc.data() : {};
      setPrecioAlta(Number(data.precioAlta ?? 3000));
      setPrecioMedia(Number(data.precioMedia ?? 2800));
      setPrecioBaja(Number(data.precioBaja ?? 2500));
    };

    const obtenerPreciosExtra = async () => {
      const doc = await firestore.collection("config").doc("precios_extra").get();
      const data = doc.exists ? doc.data() : {};
      setPrecioCamping(Number(data.campingPorPersona ?? 290));
      setPrecioDia(Number(data.diaPorPersona ?? 145));
    };

    obtenerPrecios();
    obtenerPreciosExtra();

    return () => {
      unsubReservas();
      unsubBloqueos();
      unsubUsuarios();
      unsubRegistros(); // 👈 NUEVO
    };
  }, []);

  const guardarPrecios = async () => {
    try {
      await firestore.collection("config").doc("precios").set({
        precioAlta:  Number(precioAlta),
        precioMedia: Number(precioMedia),
        precioBaja:  Number(precioBaja),
      });
      Swal.fire("Guardado", "Los precios han sido actualizados.", "success");
    } catch (error) {
      Swal.fire("Error", "No se pudo guardar. Intenta nuevamente.", "error");
    }
  };

  const guardarPreciosExtra = async () => {
    try {
      await firestore.collection("config").doc("precios_extra").set(
        {
          campingPorPersona: Number(precioCamping),
          diaPorPersona: Number(precioDia),
        },
        { merge: true }
      );
      Swal.fire("Guardado", "Precios de Camping y Por el día actualizados.", "success");
    } catch (error) {
      Swal.fire("Error", "No se pudo guardar los precios de Camping/Día.", "error");
    }
  };

  const sameDay = (a, b) => {
    if (!a || !b) return false;
    const da = new Date(a); da.setHours(0, 0, 0, 0);
    const db = new Date(b); db.setHours(0, 0, 0, 0);
    return da.getTime() === db.getTime();
  };

  const obtenerClaseDia = (date) => {
    const hoy = hoyUY(); hoy.setHours(0, 0, 0, 0);
    const fechaActual = new Date(date); fechaActual.setHours(0, 0, 0, 0);

    // 1) Bloqueos mandan
    const estaBloqueada = bloqueos.some(
      (b) => b.casa === casaSeleccionada && sameDay(b.fecha, fechaActual)
    );
    if (estaBloqueada) return "bloqueado" + (sameDay(fechaActual, hoy) ? " hoy" : "");

    // 2) ¿alguna reserva cubre este día?
    const r = reservas.find(
      (res) =>
        res.casa === casaSeleccionada &&
        fechaActual >= new Date(new Date(res.fechaInicio).setHours(0,0,0,0)) &&
        fechaActual <= new Date(new Date(res.fechaFin).setHours(0,0,0,0))
    );

    let clase = "";
    if (r) {
      const esInicio = sameDay(fechaActual, r.fechaInicio);
      const esFin    = sameDay(fechaActual, r.fechaFin);
      if (esInicio && esFin) clase = "reserva--middle";  // mismo día
      else if (esInicio)     clase = "reserva--start";   // media derecha
      else if (esFin)        clase = "reserva--end";     // media izquierda
      else                   clase = "reserva--middle";  // intermedio
    }

    if (sameDay(fechaActual, hoy)) clase += " hoy";
    return clase.trim();
  };

  const obtenerClaseDiaBloqueo = (date) => {
    const hoy = hoyUY();
    hoy.setHours(0, 0, 0, 0);
    if (date < hoy) return "";

    const isReserved = reservas.some(
      (reserva) =>
        reserva.casa === casaSeleccionada &&
        date >= reserva.fechaInicio &&
        date <= reserva.fechaFin
    );

    const isBlocked = bloqueos.some(
      (bloqueo) =>
        bloqueo.casa === casaSeleccionada &&
        bloqueo.fecha.toDateString() === date.toDateString()
    );

    const isSelected =
      (startDateBloqueo && !endDateBloqueo && date.getTime() === startDateBloqueo.getTime()) ||
      (startDateBloqueo && endDateBloqueo && date >= startDateBloqueo && date <= endDateBloqueo);

    if (isBlocked) return "bloqueado";
    if (isReserved) return "reserva--middle";
    if (isSelected) return "seleccionado";
    return "fecha-disponible";
  };

  const bloquearRangoFechas = async () => {
    const [inicio, fin] = rangoBloqueo;
    if (!casaSeleccionada || !inicio || !fin) {
      Swal.fire("Error", "Selecciona una casa y un rango válido.", "error");
      return;
    }

    const dias = eachDayOfInterval({ start: inicio, end: fin });

    const nuevasFechas = dias.filter(
      (dia) =>
        !bloqueos.some(
          (b) => b.casa === casaSeleccionada && b.fecha.toDateString() === dia.toDateString()
        )
    );

    try {
      const batch = firestore.batch();
      nuevasFechas.forEach((fecha) => {
        const ref = firestore.collection("bloqueos").doc();
        batch.set(ref, { casa: casaSeleccionada, fecha });
      });

      await batch.commit();
      Swal.fire("Bloqueadas", "Las fechas han sido bloqueadas correctamente.", "success");
      setRangoBloqueo([null, null]);
    } catch {
      Swal.fire("Error", "No se pudieron bloquear las fechas.", "error");
    }
  };

  const desbloquearRangoFechas = async () => {
    const [inicio, fin] = rangoDesbloqueo;
    if (!casaSeleccionada || !inicio || !fin) {
      Swal.fire("Error", "Selecciona una casa y un rango válido.", "error");
      return;
    }

    const dias = eachDayOfInterval({ start: inicio, end: fin });

    const bloqueosEliminar = bloqueos.filter(
      (b) =>
        b.casa === casaSeleccionada &&
        dias.some((d) => b.fecha.toDateString() === d.toDateString())
    );

    try {
      const batch = firestore.batch();
      bloqueosEliminar.forEach((bloqueo) => {
        const ref = firestore.collection("bloqueos").doc(bloqueo.id);
        batch.delete(ref);
      });

      await batch.commit();
      Swal.fire("Desbloqueadas", "Las fechas han sido desbloqueadas correctamente.", "success");
      setRangoDesbloqueo([null, null]);
    } catch {
      Swal.fire("Error", "No se pudieron desbloquear las fechas.", "error");
    }
  };

  // ====== Descripción de la reserva para los correos ======
  const describirItem = (item, coleccion) => {
    const fmtF = (x) => {
      const f = x && x.toDate ? x.toDate() : x;
      return f ? format(f, "dd/MM/yyyy", { locale: es }) : "";
    };
    if (coleccion === "reservas") {
      return {
        titulo: item.casa || "Casa",
        texto: `tu reserva de la ${item.casa || "casa"} del ${fmtF(item.fechaInicio)} al ${fmtF(item.fechaFin)}`,
        fechas: `${fmtF(item.fechaInicio)} – ${fmtF(item.fechaFin)}`,
      };
    }
    if (item.tipo === "camping") {
      return {
        titulo: "Camping",
        texto: `tu estadía de camping del ${fmtF(item.inicio)} al ${fmtF(item.fin)}`,
        fechas: `${fmtF(item.inicio)} – ${fmtF(item.fin)}`,
      };
    }
    return {
      titulo: "Día",
      texto: `tu visita por el día del ${fmtF(item.fecha)}`,
      fechas: fmtF(item.fecha),
    };
  };

  // ====== EMAIL de pago recibido (parcial o completo) ======
  async function enviarMailPago(item, coleccion, { monto, metodo, total, pagado, saldo, estado }) {
    const desc = describirItem(item, coleccion);
    const completo = estado === "confirmado";
    const asunto = completo
      ? `Pago confirmado · ${desc.titulo} · ${desc.fechas}`
      : `Pago recibido · ${desc.titulo} · ${desc.fechas}`;

    const bloqueSaldo = completo
      ? `<div style="background:#e8f5e9;border:1px solid #a5d6a7;border-radius:8px;padding:12px 14px;margin:14px 0">` +
        `<p style="margin:0">✅ Tu pago está <b>completo</b>. ¡Te esperamos!</p></div>`
      : `<div style="background:#fff8e1;border:1px solid #ffe082;border-radius:8px;padding:12px 14px;margin:14px 0">` +
        `<p style="margin:0 0 6px">Saldo pendiente: <b>${money(saldo)}</b></p>` +
        `<p style="margin:0 0 6px">📲 Enviá el comprobante del resto a <b>Ricardo Bachmann</b> por WhatsApp: ` +
        `<a href="https://wa.me/59899503445">099 503 445</a></p>` +
        `<p style="margin:0"><a href="https://www.reservascuchi.com/info#pagos">Información útil (datos para el pago)</a></p></div>`;

    const html =
      `<div style="max-width:560px;margin:0;font-family:Arial,Helvetica,sans-serif;color:#222;line-height:1.5">` +
      `<h2 style="margin:0 0 12px">${completo ? "✅ Pago confirmado" : "💳 Pago recibido"}</h2>` +
      `<p style="margin:0 0 10px">Hola ${item.nombre || ""},</p>` +
      `<p style="margin:0 0 10px">Recibimos tu pago de <b>${money(monto)}</b>${metodo ? ` (${metodo})` : ""} para ${desc.texto}.</p>` +
      `<p style="margin:0 0 4px">Total: <b>${money(total)}</b></p>` +
      `<p style="margin:0 0 4px">Pagado hasta ahora: <b>${money(pagado)}</b></p>` +
      bloqueSaldo +
      `<p style="color:#777;font-size:12px;margin-top:16px">¿Dudas? WhatsApp: ` +
      `<a href="https://wa.me/59898925827">098 925 827</a><br>Reservas Cuchilla Alta</p>` +
      `</div>`;

    const text =
      `Hola ${item.nombre || ""}\n` +
      `Recibimos tu pago de ${money(monto)}${metodo ? ` (${metodo})` : ""} para ${desc.texto}.\n` +
      `Total: ${money(total)}\nPagado hasta ahora: ${money(pagado)}\n` +
      (completo
        ? `Tu pago está completo. ¡Te esperamos!\n`
        : `Saldo pendiente: ${money(saldo)}\n` +
          `Enviá el comprobante del resto a Ricardo Bachmann por WhatsApp: 099 503 445\n`) +
      `¿Dudas? WhatsApp: 098 925 827\n`;

    await enviarMail({ to: item.email, subject: asunto, html, text });

    // Copia admin
    await enviarMail({
      to: ADMIN_EMAIL,
      subject: `${asunto} (copia admin)`,
      html:
        `<p>${completo ? "Pago completo" : "Pago parcial"} registrado por <b>${etiquetaCuenta(cuentaActual())}</b>.</p>` +
        `<ul><li>Cliente: ${item.nombre || ""} (${item.email})</li><li>${desc.titulo}: ${desc.fechas}</li>` +
        `<li>Este pago: ${money(monto)}</li><li>Total: ${money(total)} · Pagado: ${money(pagado)} · Saldo: ${money(saldo)}</li></ul>`,
      text:
        `${completo ? "Pago completo" : "Pago parcial"} – ${item.nombre || ""} (${item.email})\n` +
        `${desc.titulo}: ${desc.fechas}\nEste pago: ${money(monto)}\n` +
        `Total: ${money(total)} · Pagado: ${money(pagado)} · Saldo: ${money(saldo)}\n`,
    });
  }

  // ====== REGISTRAR un pago (total o parcial) ======
  async function registrarPago(item, coleccion) {
    const idKey = keyOf(coleccion, item.id);
    const total = totalDe(item, coleccion);
    const saldoActual = resumenDe(item, coleccion).saldo;
    const monto = Number(String(editMonto[idKey] ?? saldoActual).replace(",", "."));
    const metodo = (editMetodo[idKey] ?? "").trim();
    const nota = (editNota[idKey] ?? "").trim();
    const debeEnviarMail = !!editEnviarMail[idKey];

    if (!(monto > 0)) {
      Swal.fire("Monto inválido", "Ingresá un monto mayor a 0.", "warning");
      return;
    }

    try {
      const ref = firestore.collection(coleccion).doc(item.id);
      const por = cuentaActual();
      const ahora = new Date();
      let resultado = null;

      await firestore.runTransaction(async (tx) => {
        const snap = await tx.get(ref);
        const actual = snap.data() || {};
        const pagos = Array.isArray(actual.pagos) ? [...actual.pagos] : [];
        pagos.push({ id: String(ahora.getTime()), monto, metodo, nota, por, fecha: ahora.toISOString() });
        const pagado = sumaPagos({ pagos });
        const estado = estadoPorMontos(total, pagado);
        resultado = { pagado, saldo: Math.max(0, total - pagado), estado };
        tx.update(ref, {
          pagos,
          montoPagado: pagado,
          paymentStatus: estado,
          paymentMethod: metodo,
          paymentNote: nota,
          paymentConfirmedAt: estado === "confirmado" ? ahora : null,
          paymentConfirmedBy: estado === "confirmado" ? por : "",
          ...camposAuditoria(estado, metodo, nota, { monto }),
        });
      });

      let avisoMail = "";
      if (debeEnviarMail) {
        try {
          await enviarMailPago(item, coleccion, { monto, metodo, total, ...resultado });
          avisoMail = "<p>✉️ Se envió el correo al huésped.</p>";
        } catch (e) {
          console.error("Error al enviar correo de pago:", e);
          avisoMail = "<p>⚠️ El pago se guardó, pero no se pudo enviar el correo.</p>";
        }
      }

      setEditMonto((s) => { const n = { ...s }; delete n[idKey]; return n; });
      setEditNota((s) => { const n = { ...s }; delete n[idKey]; return n; });
      setEditEnviarMail((s) => { const n = { ...s }; delete n[idKey]; return n; });

      Swal.fire({
        icon: "success",
        title: resultado.estado === "confirmado" ? "Pago completo" : "Pago parcial registrado",
        html:
          `<div style="text-align:left">` +
          `<p>Este pago: <b>${money(monto)}</b></p>` +
          `<p>Total: ${money(total)} · Pagado: ${money(resultado.pagado)}</p>` +
          `<p>Saldo: <b>${money(resultado.saldo)}</b></p>` +
          avisoMail +
          `</div>`,
      });
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "No se pudo registrar el pago.", "error");
    }
  }

  // ====== ANULAR un pago cargado por error (queda en el historial) ======
  async function anularPago(item, coleccion, pagoId) {
    const conf = await Swal.fire({
      title: "¿Anular este pago?",
      text: "Queda registrado como anulado en el historial.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, anular",
      cancelButtonText: "No",
    });
    if (!conf.isConfirmed) return;

    try {
      const ref = firestore.collection(coleccion).doc(item.id);
      const total = totalDe(item, coleccion);
      const por = cuentaActual();
      const ahora = new Date();
      await firestore.runTransaction(async (tx) => {
        const snap = await tx.get(ref);
        const actual = snap.data() || {};
        let montoAnulado = 0;
        const pagos = (Array.isArray(actual.pagos) ? actual.pagos : []).map((p) => {
          if (p.id !== pagoId || p.anulado) return p;
          montoAnulado = Number(p.monto) || 0;
          return { ...p, anulado: true, anuladoPor: por, anuladoFecha: ahora.toISOString() };
        });
        const pagado = sumaPagos({ pagos });
        const estado = estadoPorMontos(total, pagado);
        tx.update(ref, {
          pagos,
          montoPagado: pagado,
          paymentStatus: estado,
          paymentConfirmedAt: estado === "confirmado" ? actual.paymentConfirmedAt || ahora : null,
          paymentConfirmedBy: estado === "confirmado" ? actual.paymentConfirmedBy || por : "",
          ...camposAuditoria(estado, "", "Pago anulado", { monto: montoAnulado, anulado: true }),
        });
      });
      Swal.fire("Listo", "El pago quedó anulado.", "success");
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "No se pudo anular el pago.", "error");
    }
  }

  // ====== EDITAR MONTO (solo admin): normal, acordado o sin costo ======
  async function editarMonto(item, coleccion) {
    const tipoActual = item.precioTipo || "normal";
    const normal = totalNormal(item, coleccion);
    const totalActual = totalDe(item, coleccion);

    const { value, isConfirmed } = await Swal.fire({
      title: "Monto de la reserva",
      html:
        `<div style="text-align:left;font-size:15px">` +
        `<label style="display:block;margin:8px 0"><input type="radio" name="pt" value="normal" ${tipoActual === "normal" ? "checked" : ""}> Normal (${money(normal)})</label>` +
        `<label style="display:block;margin:8px 0"><input type="radio" name="pt" value="acordado" ${tipoActual === "acordado" ? "checked" : ""}> Monto acordado</label>` +
        `<input id="pt-monto" type="number" min="0" step="1" class="swal2-input" style="margin:0 0 8px;width:100%;box-sizing:border-box" placeholder="Monto acordado en UYU" value="${tipoActual === "acordado" ? totalActual : ""}">` +
        `<label style="display:block;margin:8px 0"><input type="radio" name="pt" value="sinCosto" ${tipoActual === "sinCosto" ? "checked" : ""}> Sin costo</label>` +
        `</div>`,
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
      didOpen: () => {
        const input = document.getElementById("pt-monto");
        input.addEventListener("focus", () => {
          const acordado = document.querySelector('input[name="pt"][value="acordado"]');
          if (acordado) acordado.checked = true;
        });
      },
      preConfirm: () => {
        const tipo = document.querySelector('input[name="pt"]:checked')?.value || "normal";
        const txt = document.getElementById("pt-monto").value;
        const m = Number(txt);
        if (tipo === "acordado" && (txt === "" || !(m >= 0))) {
          Swal.showValidationMessage("Ingresá el monto acordado");
          return false;
        }
        return { tipo, monto: m };
      },
    });
    if (!isConfirmed || !value) return;

    const nuevoTotal = value.tipo === "sinCosto" ? 0 : value.tipo === "acordado" ? value.monto : normal;

    try {
      const ref = firestore.collection(coleccion).doc(item.id);
      await firestore.runTransaction(async (tx) => {
        const snap = await tx.get(ref);
        const actual = snap.data() || {};
        const pagos = Array.isArray(actual.pagos) ? actual.pagos : [];
        const pagado = sumaPagos({ pagos });
        const confirmadoAntiguo = pagos.length === 0 && actual.paymentStatus === "confirmado";
        const estado =
          value.tipo === "sinCosto"
            ? "sinCosto"
            : confirmadoAntiguo
            ? "confirmado"
            : estadoPorMontos(nuevoTotal, pagado);
        tx.update(ref, {
          total: nuevoTotal,
          precioTipo: value.tipo,
          montoPagado: pagado,
          paymentStatus: estado,
          ...camposAuditoria(estado, "", `Monto: ${value.tipo === "sinCosto" ? "sin costo" : money(nuevoTotal)}${value.tipo === "acordado" ? " (acordado)" : ""}`),
        });
      });
      Swal.fire("Guardado", "El monto de la reserva se actualizó.", "success");
    } catch (e) {
      console.error(e);
      Swal.fire("Error", "No se pudo actualizar el monto.", "error");
    }
  }

  // ====== Bloque de pago de cada fila (casas y camping/día) ======
  const renderBloquePago = (item, coleccion, pasada) => {
    const idKey = keyOf(coleccion, item.id);
    const r = resumenDe(item, coleccion);
    const pagos = Array.isArray(item.pagos) ? item.pagos : [];
    const completo = r.estado === "confirmado" || r.estado === "sinCosto";

    return (
      <>
        <div className="pago-montos">
          {r.estado === "sinCosto" ? (
            <span>Monto: <b>Sin costo</b></span>
          ) : (
            <span>
              Total: <b>{money(r.total)}</b>
              {item.precioTipo === "acordado" && (
                <span className="badge badge--acordado">Monto acordado</span>
              )}
              {" · "}Pagado: <b>{money(r.pagado)}</b>{" · "}
              {r.saldo > 0 ? (
                <span className="pago-saldo">Saldo: <b>{money(r.saldo)}</b></span>
              ) : (
                <span className="pago-ok">Sin saldo pendiente</span>
              )}
            </span>
          )}
          {!soloCobros && (
            <button type="button" className="btn-link-pago" onClick={() => editarMonto(item, coleccion)}>
              Editar monto
            </button>
          )}
        </div>

        {pagos.length > 0 && (
          <ul className="pagos-lista">
            {pagos.map((p) => (
              <li key={p.id} className={p.anulado ? "pago-anulado" : ""}>
                <span>
                  {fechaHoraUY(p.fecha)} · <b>{money(p.monto)}</b>
                  {p.metodo ? ` · ${p.metodo}` : ""}
                  {p.nota ? ` · ${p.nota}` : ""}
                  {` · ${etiquetaCuenta(p.por)}`}
                </span>
                {p.anulado ? (
                  <em className="pago-anulado-txt">anulado ({etiquetaCuenta(p.anuladoPor)})</em>
                ) : (
                  <button type="button" className="btn-link-pago" onClick={() => anularPago(item, coleccion, p.id)}>
                    Anular
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="fila-pago">
          {!completo && (
            <div className="pago-controls">
              <label className="pc-field">
                <span className="pc-label">Monto:</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="pc-monto"
                  value={editMonto[idKey] ?? String(r.saldo)}
                  onChange={(e) => setMonto(idKey, e.target.value)}
                />
              </label>

              <label className="pc-field">
                <span className="pc-label">Método:</span>
                <select
                  value={editMetodo[idKey] ?? ""}
                  onChange={(e) => setMetodo(idKey, e.target.value)}
                >
                  <option value="">(seleccionar)</option>
                  <option value="Transferencia a Cta. Itaú">Transferencia a Cta. Itaú</option>
                  <option value="Depósito ABITAB">Depósito ABITAB</option>
                  <option value="Cooperativa (Colonias)">Cooperativa (Colonias)</option>
                  <option value="Efectivo">Efectivo</option>
                </select>
              </label>

              <input
                className="pc-nota"
                placeholder="Nota (opcional)"
                value={editNota[idKey] ?? ""}
                onChange={(e) => setNota(idKey, e.target.value)}
              />

              <label className="pc-check">
                <input
                  type="checkbox"
                  checked={!!editEnviarMail[idKey]}
                  onChange={(e) => setMail(idKey, e.target.checked)}
                />
                Enviar correo
              </label>

              <button className="guardar-pago-btn" onClick={() => registrarPago(item, coleccion)}>
                Registrar pago
              </button>
            </div>
          )}

          <div className="pago-status">
            {pasada && <span className="badge badge--finalizada">Finalizada</span>}
            <span className={`badge badge--${r.estado}`}>{etiquetaEstado(r.estado)}</span>
            {renderAuditoria(item)}
          </div>
        </div>
      </>
    );
  };

  // ====== Cancelar reserva ======
  const cancelarReserva = (id) => {
    Swal.fire({
      title: "¿Cancelar esta reserva?",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, cancelar",
      cancelButtonText: "No",
    }).then((result) => {
      if (result.isConfirmed) {
        firestore
          .collection("reservas")
          .doc(id)
          .delete()
          .then(() => Swal.fire("Cancelada", "Reserva cancelada.", "info"))
          .catch(() => Swal.fire("Error", "No se pudo cancelar.", "error"));
      }
    });
  };

  // ====== Filtrados y export ======
  const reservasFiltradas = reservas.filter((reserva) => {
    const texto = searchQuery.toLowerCase();

    const pasaTexto =
      reserva.nombre.toLowerCase().includes(texto) ||
      reserva.email.toLowerCase().includes(texto) ||
      reserva.casa.toLowerCase().includes(texto) ||
      reserva.fechaInicio.toLocaleString("es-ES", { month: "long" }).toLowerCase().includes(texto) ||
      reserva.fechaFin.toLocaleString("es-ES", { month: "long" }).toLowerCase().includes(texto);

    return pasaTexto;
  });

  const reservasOrdenadas = [...reservasFiltradas].sort(
    (a, b) => b.fechaInicio - a.fechaInicio
  );

  // Solo para la pestaña Pagos: aplica además el filtro de estado de pago
  const reservasPagos = reservasOrdenadas.filter(
    (r) => !filtroEstado || resumenDe(r, "reservas").estado === filtroEstado
  );

  const usuariosFiltrados = usuarios.filter((usuario) => {
    const texto = searchUsuario.toLowerCase();
    return (
      usuario.nombre.toLowerCase().includes(texto) ||
      usuario.apellido.toLowerCase().includes(texto) ||
      usuario.email.toLowerCase().includes(texto) ||
      (usuario.congregacion && usuario.congregacion.toLowerCase().includes(texto))
    );
  });

  // === PAGOS: contadores por estado (para los pills) ===
  const pagosCount = (() => {
    const t = { pendiente: 0, parcial: 0, confirmado: 0, sinCosto: 0, todos: 0 };
    reservas.forEach((r) => { t[resumenDe(r, "reservas").estado]++; t.todos++; });
    registros.forEach((r) => { t[resumenDe(r, "registros").estado]++; t.todos++; });
    return t;
  })();

  // Exporta TODO: todas las reservas de casas + todos los registros (camping y día),
  // sin aplicar los filtros de la vista, con todas las columnas.
  const exportarExcel = () => {
    // --- Hoja 1: Reservas de casas (todas) ---
    const casasDatos = [...reservas]
      .sort((a, b) => b.fechaInicio - a.fechaInicio)
      .map((r) => {
        const rp = resumenDe(r, "reservas");
        return {
          Nombre: r.nombre || "",
          Correo: r.email || "",
          Celular: celularDe(r.email),
          Casa: r.casa || "",
          "Fecha Inicio": r.fechaInicio ? format(r.fechaInicio, "dd/MM/yyyy") : "",
          "Fecha Fin": r.fechaFin ? format(r.fechaFin, "dd/MM/yyyy") : "",
          Noches:
            r.fechaInicio && r.fechaFin
              ? Math.max(0, differenceInDays(r.fechaFin, r.fechaInicio))
              : "",
          "Tipo de precio": r.precioTipo === "acordado" ? "Acordado" : r.precioTipo === "sinCosto" ? "Sin costo" : "Normal",
          "Monto (UYU)": rp.total,
          "Pagado (UYU)": rp.pagado,
          "Saldo (UYU)": rp.saldo,
          "Estado Pago": etiquetaEstado(rp.estado),
          Pagos: detallePagos(r),
          "Método Pago": r.paymentMethod || "",
          Nota: r.paymentNote || "",
          "Pago confirmado el": r.paymentConfirmedAt ? format(r.paymentConfirmedAt, "dd/MM/yyyy") : "",
          "Confirmado por": etiquetaCuenta(r.paymentConfirmedBy),
          "Último cambio por": etiquetaCuenta(r.paymentUpdatedBy),
          "Último cambio el": fechaHoraUY(r.paymentUpdatedAt),
          "Historial pago": historialTexto(r),
          "Cancelación solicitada": r.cancelRequested ? "Sí" : "No",
          Origen: r.origen || "",
          "Creado por": r.creadoPorEmail || "",
        };
      });

    // --- Hoja 2: Registros de camping y día (todos) ---
    const regsDatos = [...registros]
      .sort(
        (a, b) =>
          (toDate(b.creadoEn)?.getTime?.() || 0) - (toDate(a.creadoEn)?.getTime?.() || 0)
      )
      .map((r) => {
        const esCamping = r.tipo === "camping";
        const inicio = esCamping ? toDate(r.inicio) : null;
        const fin = esCamping ? toDate(r.fin) : null;
        const fecha = !esCamping ? toDate(r.fecha) : null;
        const rp = resumenDe(r, "registros");
        return {
          Tipo: esCamping ? "Camping" : "Día",
          Nombre: r.nombre || "",
          Correo: r.email || "",
          Celular: celularDe(r.email),
          Personas: Number(r.personas || 0),
          Noches: esCamping ? Number(r.noches || 0) : "",
          Inicio: inicio ? format(inicio, "dd/MM/yyyy") : "",
          Fin: fin ? format(fin, "dd/MM/yyyy") : "",
          Fecha: fecha ? format(fecha, "dd/MM/yyyy") : "",
          "Tipo de precio": r.precioTipo === "acordado" ? "Acordado" : r.precioTipo === "sinCosto" ? "Sin costo" : "Normal",
          "Total (UYU)": rp.total,
          "Pagado (UYU)": rp.pagado,
          "Saldo (UYU)": rp.saldo,
          "Estado Pago": etiquetaEstado(rp.estado),
          Pagos: detallePagos(r),
          "Método Pago": r.paymentMethod || "",
          Nota: r.paymentNote || "",
          "Creado el": r.creadoEn ? format(r.creadoEn, "dd/MM/yyyy") : "",
          "Último cambio por": etiquetaCuenta(r.paymentUpdatedBy),
          "Último cambio el": fechaHoraUY(r.paymentUpdatedAt),
          "Historial pago": historialTexto(r),
        };
      });

    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, XLSX.utils.json_to_sheet(casasDatos), "Reservas Casas");
    XLSX.utils.book_append_sheet(libro, XLSX.utils.json_to_sheet(regsDatos), "Camping y Día");
    XLSX.writeFile(libro, `reservas_completo_${format(hoyUY(), "yyyy-MM-dd")}.xlsx`);
  };

  return (
    <div className="container">
      <h2>{soloCobros ? "Panel de Cobros" : "Panel de Administración"}</h2>
      {soloCobros ? (
        <div className="tabs">
          <button className="tab activo" onClick={() => setTabActivo("pagos")}>
            Pagos
          </button>
        </div>
      ) : (
      <div className="tabs">
        <button
          className={tabActivo === "reservas" ? "tab activo" : "tab"}
          onClick={() => setTabActivo("reservas")}
        >
          Reservas (Casas)
        </button>
        <button
          className={tabActivo === "registros" ? "tab activo" : "tab"}
          onClick={() => setTabActivo("registros")}
        >
          Registros (Cg/Día)
        </button>
        <button
          className={tabActivo === "pagos" ? "tab activo" : "tab"}
          onClick={() => setTabActivo("pagos")}
        >
          Pagos
        </button>
        <button
          className={tabActivo === "cancelaciones" ? "tab activo" : "tab"}
          onClick={() => setTabActivo("cancelaciones")}
        >
          Cancela- ciones
        </button>

        <button
          className={tabActivo === "disponibilidad" ? "tab activo" : "tab"}
          onClick={() => setTabActivo("disponibilidad")}
        >
          Disponi- bilidad
        </button>
        <button
          className={tabActivo === "usuarios" ? "tab activo" : "tab"}
          onClick={() => setTabActivo("usuarios")}
        >
          Usuarios
        </button>
        <button
          className={tabActivo === "bloqueo" ? "tab activo" : "tab"}
          onClick={() => setTabActivo("bloqueo")}
        >
          Bloqueo Fechas
        </button>
        <button
          className={tabActivo === "precios" ? "tab activo" : "tab"}
          onClick={() => setTabActivo("precios")}
        >
          Precios
        </button>
      </div>
      )}

      {tabVisible === "reservas" && (
        <div className="tab-contenido">
          {/* Título + Exportar */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "30px",
            }}
          >
            <h2>Todas las Reservas de CASAS</h2>
            <button className="excel-btn" onClick={exportarExcel}>
              Exportar todo a Excel
            </button>
          </div>

          {/* Buscador (solo texto) */}
          <div className="filtros-row" style={{ gap: 12 }}>
            <input
              type="text"
              placeholder="Buscar por nombre, correo, casa o mes"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="busqueda-input"
            />
          </div>

          {/* Listado */}
          <ul>
            {reservasOrdenadas.map((reserva) => {
              const hoy = hoyUY(); hoy.setHours(0, 0, 0, 0);
              const fin = new Date(reserva.fechaFin); fin.setHours(0, 0, 0, 0);
              const pasada = fin < hoy;                        // 👈 “reserva pasada” por día calendario
              const estadoPago = resumenDe(reserva, "reservas").estado;

              return (
                <li
                  key={reserva.id}
                  className={`reserva-item ${pasada ? "reserva-pasada" : ""}`}
                >
                  <span
                    className="reserva-text"
                    style={pasada ? { textDecoration: "line-through" } : undefined} // fallback visual
                  >
                    {reserva.nombre} reservó la <strong>{reserva.casa}</strong> del{" "}
                    {reserva.fechaInicio.toLocaleDateString("es-ES")} al{" "}
                    {reserva.fechaFin.toLocaleDateString("es-ES")}
                  </span>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginTop: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    {pasada && <span className="badge badge--finalizada">Finalizada</span>}

                      {/* NUEVO: badge de cancelación */}
                    {reserva.cancelRequested && (
                      <span className="badge badge--cancel" style={{ marginLeft: 8 }}>
                        Cancelación solicitada
                      </span>
                    )}

                    <span className={`badge badge--${estadoPago}`}>
                      {etiquetaEstado(estadoPago)}
                    </span>

                    {/* Si querés ocultar este botón para pasadas, cambiá por: {!pasada && (...)} */}
                    <button className="cancelar-btn" onClick={() => cancelarReserva(reserva.id)}>
                      Cancelar Reserva
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {tabVisible === "registros" && (
        <div className="tab-contenido">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "30px",
            }}
          >
            <h2>Todos los Registros de CAMPING y estadías por el DÍA</h2>
            <button className="excel-btn" onClick={exportarExcel}>
              Exportar todo a Excel
            </button>
          </div>

          {/* Buscador (reusa searchQuery) */}
          <div className="filtros-row" style={{ gap: 12 }}>
            <input
              type="text"
              placeholder="Buscar por nombre, correo o tipo"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="busqueda-input"
            />
          </div>

          <ul>
            {registrosOrdenados
              .filter((r) => {
                const t = (searchQuery || "").toLowerCase();
                if (!t) return true;
                return (
                  (r.nombre || "").toLowerCase().includes(t) ||
                  (r.email || "").toLowerCase().includes(t) ||
                  (r.tipo || "").toLowerCase().includes(t)
                );
              })
              .map((r) => {
                const estado = resumenDe(r, "registros").estado;
                const hoy = hoyUY(); hoy.setHours(0, 0, 0, 0);
                const esCamping = r.tipo === "camping";
                const finReg = toDate(esCamping ? r.fin : r.fecha);
                const finReg0 = finReg ? new Date(finReg) : null;
                if (finReg0) finReg0.setHours(0, 0, 0, 0);
                const pasada = !!finReg0 && finReg0 < hoy;
                return (
                  <li key={r.id} className={`reserva-item ${pasada ? "reserva-pasada" : ""}`}>
                    <span
                      className="reserva-text"
                      style={pasada ? { textDecoration: "line-through" } : undefined}
                    >
                      {lineaRegistro(r)}
                    </span>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginTop: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      {/* NUEVO: badge de eliminación/cancelación pendiente */}
                      {(r.deleteRequested || r.eliminacionPendiente || r.cancelRequested) && (
                        <span className="badge badge--cancel" style={{ marginRight: 4 }}>
                          Eliminación pendiente
                        </span>
                      )}
                      
                      <span className={`badge badge--${estado}`}>
                        {etiquetaEstado(estado)}
                      </span>

                      <button
                        className="cancelar-btn"
                        onClick={() => eliminarRegistro(r.id)}
                      >
                        Eliminar registro
                      </button>
                    </div>
                  </li>
                );
              })}
            {registrosOrdenados.length === 0 && <p>No hay registros.</p>}
          </ul>
        </div>
      )}

      {tabVisible === "disponibilidad" && (
        <div className="tab-contenido">
          <h2>Verificar disponibilidad de Casas</h2>

          <div className="form-group">
            <label>Seleccionar Casa:</label>
            <select
              value={casaSeleccionada}
              onChange={(e) => setCasaSeleccionada(e.target.value)}
            >
              <option value="">-- Selecciona una casa --</option>
              <option value="Casa 1">Casa 1</option>
              <option value="Casa 2">Casa 2</option>
              <option value="Casa 3">Casa 3</option>
              <option value="Casa 4">Casa 4</option>
            </select>
          </div>

          {casaSeleccionada && (
            <>
              <div className="leyenda-disponibilidad">
                <span><span className="color-circulo reserva"></span> Ocupado</span>
                <span><span className="color-circulo bloqueado"></span> Fecha bloqueada</span>
                {/* opcional: muestra de medio día
                <span><span className="color-circulo medio"></span> Check-in/out</span>
                */}
              </div>
              <div className="calendario-admin">
                {[0, 1, 2, 3].map((i) => (
                  <DatePicker
                    key={i}
                    inline
                    locale="es"
                    disabled
                    selected={startOfMonth(addMonths(hoyUY(), i))}
                    dayClassName={obtenerClaseDia}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {tabVisible === "pagos" && (
        <div className="tab-contenido">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "30px",
            }}
          >
            <h2>Estado de Pagos</h2>
            <button className="excel-btn" onClick={exportarExcel}>
              Exportar todo a Excel
            </button>
          </div>

          {/* Filtros tipo "pills" (como Cancelaciones) */}
          <div className="cancel-toolbar">
            <div className="filters">
              <button
                className={filtroEstado === "" ? "pill active" : "pill"}
                onClick={() => setFiltroEstado("")}
                type="button"
              >
                Todas ({pagosCount.todos})
              </button>
              <button
                className={filtroEstado === "pendiente" ? "pill active" : "pill"}
                onClick={() => setFiltroEstado("pendiente")}
                type="button"
              >
                Pendientes ({pagosCount.pendiente})
              </button>
              <button
                className={filtroEstado === "parcial" ? "pill active" : "pill"}
                onClick={() => setFiltroEstado("parcial")}
                type="button"
              >
                Parciales ({pagosCount.parcial})
              </button>
              <button
                className={filtroEstado === "confirmado" ? "pill active" : "pill"}
                onClick={() => setFiltroEstado("confirmado")}
                type="button"
              >
                Confirmados ({pagosCount.confirmado})
              </button>
              <button
                className={filtroEstado === "sinCosto" ? "pill active" : "pill"}
                onClick={() => setFiltroEstado("sinCosto")}
                type="button"
              >
                Sin costo ({pagosCount.sinCosto})
              </button>
            </div>

            <input
              className="cancel-search"
              type="search"
              placeholder="Buscar por nombre, correo, casa o tipo"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Sub-pestañas: Casas / Camping y Día */}
          <div className="filters" style={{ margin: "14px 0 4px" }}>
            <button
              className={subTabPagos === "casas" ? "pill active" : "pill"}
              onClick={() => setSubTabPagos("casas")}
              type="button"
            >
              🏠 Casas
            </button>
            <button
              className={subTabPagos === "registros" ? "pill active" : "pill"}
              onClick={() => setSubTabPagos("registros")}
              type="button"
            >
              ⛺ Camping y Día
            </button>
          </div>

          {subTabPagos === "casas" && (<>
          {/* ===== Reservas (casas) ===== */}
          <h4 style={{ marginTop: 20 }}>Reservas (casas)</h4>
          <ul>
            {reservasPagos.map((reserva) => {
              const hoy = hoyUY(); hoy.setHours(0, 0, 0, 0);
              const fin = new Date(reserva.fechaFin); fin.setHours(0, 0, 0, 0);
              const pasada = fin < hoy;

              return (
                <li
                  key={reserva.id}
                  className={`reserva-item ${pasada ? "reserva-pasada" : ""}`}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span
                      className="reserva-text"
                      style={pasada ? { textDecoration: "line-through" } : undefined}
                    >
                      {reserva.nombre} reservó la <strong>{reserva.casa}</strong> del{" "}
                      {reserva.fechaInicio.toLocaleDateString("es-ES")} al{" "}
                      {reserva.fechaFin.toLocaleDateString("es-ES")}
                    </span>

                    {reserva.cancelRequested && (
                      <span className="badge badge--cancel" style={{ marginLeft: 8 }}>
                        Cancelación solicitada
                      </span>
                    )}
                  </div>

                  {renderContacto(reserva.email)}

                  {renderBloquePago(reserva, "reservas", pasada)}
                </li>
              );
            })}
          </ul>

          </>)}

          {subTabPagos === "registros" && (<>
          {/* ===== Registros (camping y día) ===== */}
          <h4 style={{ marginTop: 24 }}>Registros (camping y día)</h4>
          <ul>
            {[...registros]
              .filter((r) => {
                const texto = (searchQuery || "").toLowerCase();
                const pasaTexto =
                  (r.nombre || "").toLowerCase().includes(texto) ||
                  (r.email || "").toLowerCase().includes(texto) ||
                  (r.tipo || "").toLowerCase().includes(texto);
                const pasaEstado = !filtroEstado || resumenDe(r, "registros").estado === filtroEstado;
                return pasaTexto && pasaEstado;
              })
              .sort(
                (a, b) =>
                  (toDate(b.creadoEn)?.getTime?.() || 0) -
                  (toDate(a.creadoEn)?.getTime?.() || 0)
              )
              .map((r) => {
                const esCamping = r.tipo === "camping";
                const hoy = hoyUY(); hoy.setHours(0, 0, 0, 0);
                const finReg = toDate(esCamping ? r.fin : r.fecha);
                const finReg0 = finReg ? new Date(finReg) : null;
                if (finReg0) finReg0.setHours(0, 0, 0, 0);
                const pasada = !!finReg0 && finReg0 < hoy;
                const rango = esCamping
                  ? `${r.inicio ? format(r.inicio, "dd/MM/yyyy") : "?"} – ${r.fin ? format(r.fin, "dd/MM/yyyy") : "?"}`
                  : r.fecha
                  ? format(r.fecha, "dd/MM/yyyy")
                  : "?";
                const titulo = esCamping
                  ? `Camping · ${r.personas} pers · ${r.noches} noche${(r.noches || 0) > 1 ? "s" : ""}`
                  : `Por el día · ${r.personas} pers`;

                return (
                  <li key={r.id} className={`reserva-item ${pasada ? "reserva-pasada" : ""}`}>
                    <span className="reserva-text">
                      {r.nombre || r.email} — <b>{titulo}</b> — {rango}
                    </span>

                    {(r.cancelRequested || r.deleteRequested || r.eliminacionPendiente) && (
                      <div style={{ margin: "4px 0" }}>
                        <span className="badge badge--cancel">Cancelación solicitada</span>
                      </div>
                    )}
                    {renderContacto(r.email)}

                    {renderBloquePago(r, "registros", pasada)}
                  </li>
                );
              })}
          </ul>
          </>)}
        </div>
      )}

      {tabVisible === "usuarios" && (
        <div className="tab-contenido">
          <h2>Listado de Usuarios</h2>
          <input
            type="text"
            placeholder="Buscar por nombre, apellido, correo o congregación"
            value={searchUsuario}
            onChange={(e) => setSearchUsuario(e.target.value)}
            className="busqueda-input"
          />
          <ul>
            {usuariosFiltrados.map((usuario) => (
              <li key={usuario.id} className="usuario-item">
                <span className="usuario-info">
                  {usuario.nombre} {usuario.apellido} - {usuario.email}
                  {usuario.celular ? ` - ${usuario.celular}` : ""}
                  {usuario.congregacion ? ` - ${usuario.congregacion}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {tabVisible === "bloqueo" && (
        <div className="tab-contenido">
          <h2>Bloqueo de Fechas</h2>

          <div className="bloqueo-contenedor">
            <h4>Bloquear Fechas</h4>
            <div className="form-group">
              <label>Casa:</label>
              <select
                value={casaSeleccionada}
                onChange={(e) => setCasaSeleccionada(e.target.value)}
              >
                <option value="">-- Selecciona una casa --</option>
                <option value="Casa 1">Casa 1</option>
                <option value="Casa 2">Casa 2</option>
                <option value="Casa 3">Casa 3</option>
                <option value="Casa 4">Casa 4</option>
              </select>
            </div>

            {casaSeleccionada && (
              <>
                <div className="leyenda">
                  <div>
                    <span className="leyenda-color reserva"></span> Ocupado
                  </div>
                  <div>
                    <span className="leyenda-color bloqueado"></span> Bloqueado
                  </div>
                  <div>
                    <span className="leyenda-color fecha-disponible"></span> Disponible
                  </div>
                </div>

                <div className="form-group">
                  <label>Rango para Bloquear:</label>
                  <DatePicker
                    selected={startDateBloqueo}
                    onChange={(dates) => {
                      const [start, end] = dates;
                      setStartDateBloqueo(start);
                      setEndDateBloqueo(end);
                      setRangoBloqueo([start, end]);
                    }}
                    startDate={startDateBloqueo}
                    endDate={endDateBloqueo}
                    selectsRange
                    inline
                    locale="es"
                    dayClassName={obtenerClaseDiaBloqueo}
                  />
                </div>

                <button className="bloquear-btn" onClick={bloquearRangoFechas}>
                  Bloquear Fechas
                </button>
              </>
            )}
          </div>

          {casaSeleccionada && (
            <div className="bloqueo-contenedor">
              <h4>Desbloquear Fechas</h4>
              <div className="form-group">
                <label>Rango para Desbloquear:</label>
                <DatePicker
                  selected={rangoDesbloqueo[0]}
                  onChange={(dates) => {
                    setRangoDesbloqueo(dates);
                  }}
                  startDate={rangoDesbloqueo[0]}
                  endDate={rangoDesbloqueo[1]}
                  selectsRange
                  inline
                  locale="es"
                  dayClassName={obtenerClaseDiaBloqueo}
                />
              </div>

              <button className="desbloquear-btn" onClick={desbloquearRangoFechas}>
                Desbloquear Fechas
              </button>
            </div>
          )}
        </div>
      )}

      {tabVisible === "precios" && (
        <div className="tab-contenido">
          <h2>Editar Precios — Casas</h2>
          <div className="form-group" style={{ maxWidth: "360px", display: "grid", gap: 10 }}>
            <label>Temporada Alta (enero/febrero):</label>
            <input type="number" value={precioAlta} onChange={(e) => setPrecioAlta(e.target.value)} />

            <label>Temporada Media (diciembre/marzo):</label>
            <input type="number" value={precioMedia} onChange={(e) => setPrecioMedia(e.target.value)} />

            <label>Temporada Baja (resto del año):</label>
            <input type="number" value={precioBaja} onChange={(e) => setPrecioBaja(e.target.value)} />

            <button onClick={guardarPrecios} style={{ marginTop: 10 }}>
              Guardar precios de casas
            </button>
          </div>

          <hr style={{ margin: "24px 0" }} />

          <h2>Editar Precios — Camping y estadía por el Día</h2>
          <div className="form-group" style={{ maxWidth: "360px", display: "grid", gap: 10 }}>
            <label>Camping (por persona / noche):</label>
            <input
              type="number"
              value={precioCamping}
              onChange={(e) => setPrecioCamping(e.target.value)}
            />

            <label>Por el día (por persona):</label>
            <input
              type="number"
              value={precioDia}
              onChange={(e) => setPrecioDia(e.target.value)}
            />

            <button onClick={guardarPreciosExtra} style={{ marginTop: 10 }}>
              Guardar precios Camping / Día
            </button>
          </div>
        </div>
      )}

      {tabVisible === "cancelaciones" && (
        <div className="tab-contenido">
          <AdminCancelaciones />
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;